import { defineEventHandler, getHeader, setHeader, parseCookies } from 'h3'

/**
 * React Native Headers Middleware
 * 
 * This middleware ensures that React Native headers are properly forwarded
 * to all API requests. It detects React Native client headers in the initial
 * page request and ensures they're available for subsequent API calls.
 */
export default defineEventHandler((event) => {
  // Only handle API requests
  if (!event.path.startsWith('/api/')) return

  // Get proper parsed cookies
  const parsedCookies = parseCookies(event)
  const headers = event.node.req.headers
  
  // CRITICAL: Look for SITEACTIVEID in multiple sources with priority order:
  // 1. Header (x-go-siteactiveid)
  // 2. Cookie (SITEACTIVEID)
  // 3. Debug headers (go-debug-headers containing x-go-siteactiveid)
  
  let siteActiveId = null
  
  // 1. Try to get from header first (highest priority)
  if (headers['x-go-siteactiveid']) {
    siteActiveId = headers['x-go-siteactiveid']
    console.log('[RN Headers Middleware] Found SITEACTIVEID in header:', siteActiveId)
  } 
  // 2. Try to get from cookies next
  else if (parsedCookies.SITEACTIVEID) {
    siteActiveId = parsedCookies.SITEACTIVEID
    // CRITICAL: Always set the header from the cookie for API requests
    setHeader(event, 'x-go-siteactiveid', siteActiveId)
    console.log('[RN Headers Middleware] Added x-go-siteactiveid from cookie:', siteActiveId)
  }
  // 3. Try to get from debug headers last
  else if (parsedCookies['go-debug-headers']) {
    try {
      const debugHeaders = JSON.parse(decodeURIComponent(parsedCookies['go-debug-headers']))
      if (debugHeaders && debugHeaders['x-go-siteactiveid']) {
        siteActiveId = debugHeaders['x-go-siteactiveid']
        setHeader(event, 'x-go-siteactiveid', siteActiveId)
        console.log('[RN Headers Middleware] Added x-go-siteactiveid from debug headers:', siteActiveId)
      }
    } catch (e) {
      console.warn('[RN Headers Middleware] Failed to parse debug headers:', e)
    }
  }
  
  // Always log the request path and SITEACTIVEID status for debugging
  console.log(`[RN Headers Middleware] Request: ${event.path}`, {
    hasSiteActiveIdHeader: !!headers['x-go-siteactiveid'],
    hasSiteActiveIdCookie: !!parsedCookies.SITEACTIVEID,
    finalSiteActiveId: siteActiveId
  })

  // Also handle other React Native headers from debug-headers
  if (parsedCookies['go-debug-headers']) {
    try {
      const debugHeaders = JSON.parse(decodeURIComponent(parsedCookies['go-debug-headers']))
      
      // Forward any React Native headers found in debug headers
      for (const [key, value] of Object.entries(debugHeaders)) {
        // Only set headers that start with x-go- and aren't already present
        if (key.toLowerCase().startsWith('x-go-') && 
            key.toLowerCase() !== 'x-go-siteactiveid' && // Skip SITEACTIVEID as we handled it above
            !headers[key.toLowerCase()]) {
          setHeader(event, key.toLowerCase(), String(value))
        }
      }
      
      // Always set x-go-react-native-client header for React Native API requests
      if (debugHeaders['x-go-react-native-client'] === 'true' && !headers['x-go-react-native-client']) {
        setHeader(event, 'x-go-react-native-client', 'true')
      }
    } catch (e) {
      console.warn('[RN Headers Middleware] Failed to parse debug headers:', e)
    }
  }
})
